import { createFileRoute } from '@tanstack/react-router'
import { portfolioData } from '../data/portfolio'
import PreviewLanding from '@/components/preview/PreviewLanding'
import { getLatestPosts } from '@/lib/postServerFns'

const baseUrl = 'https://www.suriyaprakash.in'
const personId = `${baseUrl}/#person`
const websiteId = `${baseUrl}/#website`

export const Route = createFileRoute('/')({
  loader: () => getLatestPosts(),
  head: () => {
    const { personal } = portfolioData
    const imageUrl = `${baseUrl}${personal.image}`

    return {
      meta: [
        { title: personal.seoTitle },
        { name: 'title', content: personal.seoTitle },
        { name: 'description', content: personal.seoDescription },
        { name: 'robots', content: 'index, follow, max-image-preview:large' },
        { name: 'author', content: personal.name },
        { name: 'geo.region', content: 'IN-TN' },
        {
          name: 'geo.placename',
          content: 'Erode, Tamil Nadu, India',
        },
        {
          name: 'ai-summary',
          content:
            'Suriyaprakash Somu is a full-stack developer based in Erode, Tamil Nadu, building React and Node.js applications with access control, schema-driven forms and production backends. Explore case studies, interactive React component demos and engineering articles on HTTP, TLS, DNS, authentication and production debugging. Open to full-time developer roles and selected freelance projects.',
        },
        { property: 'og:title', content: personal.seoTitle },
        { property: 'og:description', content: personal.seoDescription },
        { property: 'og:type', content: 'profile' },
        { property: 'og:url', content: baseUrl },
        { property: 'og:image', content: `${baseUrl}/og/default.png` },
        { name: 'twitter:title', content: personal.seoTitle },
        { name: 'twitter:description', content: personal.seoDescription },
        { name: 'twitter:image', content: `${baseUrl}/og/default.png` },
      ],
      links: [
        { rel: 'canonical', href: baseUrl },
        {
          rel: 'alternate',
          type: 'application/rss+xml',
          title: 'RSS',
          href: `${baseUrl}/rss.xml`,
        },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Person',
                '@id': personId,
                name: personal.name,
                alternateName: [
                  personal.shortName,
                  'Suriya Prakash',
                  'Suriyaprakash Somu',
                ],
                description: personal.seoDescription,
                jobTitle: personal.role,
                url: baseUrl,
                image: imageUrl,
                email: personal.email,
                telephone: personal.phone,
                sameAs: personal.socials.map((social) => social.url),
                address: {
                  '@type': 'PostalAddress',
                  addressLocality: 'Erode',
                  addressRegion: 'Tamil Nadu',
                  addressCountry: 'IN',
                },
                homeLocation: {
                  '@type': 'Place',
                  name: personal.location,
                  address: {
                    '@type': 'PostalAddress',
                    addressLocality: 'Erode',
                    addressRegion: 'Tamil Nadu',
                    addressCountry: 'IN',
                  },
                },
                nationality: {
                  '@type': 'Country',
                  name: 'India',
                },
                hasOccupation: {
                  '@type': 'Occupation',
                  name: personal.role,
                  occupationLocation: {
                    '@type': 'Country',
                    name: 'India',
                  },
                  skills: [
                    'React',
                    'Node.js',
                    'TypeScript',
                    'Fastify',
                    'Next.js',
                    'PostgreSQL',
                    'MySQL',
                    'RBAC',
                    'Schema-Based Forms',
                    'ERP Systems',
                  ].join(', '),
                },
                knowsAbout: [
                  'React',
                  'Node.js',
                  'Express.js',
                  'TypeScript',
                  'MySQL',
                  'PostgreSQL',
                  'Fastify',
                  'Next.js',
                  'Role-Based Access Control',
                  'Schema-Based Form Rendering',
                  'Dynamic Query Builders',
                  'CASL Authorization',
                  'Drizzle ORM',
                  'ERP Systems',
                  'Full-Stack Web Development',
                ],
              },
              {
                '@type': 'WebSite',
                '@id': websiteId,
                name: personal.name,
                alternateName: [personal.shortName, 'Suriya Prakash'],
                url: baseUrl,
                inLanguage: 'en-IN',
                author: { '@id': personId },
                publisher: { '@id': personId },
              },
              ...portfolioData.services.map((service, index) => ({
                '@type': 'Service',
                '@id': `${baseUrl}/#service-${index + 1}`,
                name: service.title,
                description: service.description,
                url: `${baseUrl}/#p-services`,
                provider: { '@id': personId },
                areaServed: 'Worldwide',
              })),
              {
                '@type': 'ProfilePage',
                '@id': `${baseUrl}/#profilepage`,
                name: `${personal.name} - ${personal.role}`,
                description: personal.seoDescription,
                url: baseUrl,
                inLanguage: 'en-IN',
                isPartOf: { '@id': websiteId },
                mainEntity: { '@id': personId },
                about: { '@id': personId },
              },
            ],
          }),
        },
      ],
    }
  },
  component: LandingPage,
})

function LandingPage() {
  const posts = Route.useLoaderData()
  return <PreviewLanding posts={posts} />
}
